/**
 * Rating Slider Component
 *
 * Styled 1-10 rating slider with color gradient for the relationship tracker.
 * Shows current value prominently with descriptive label and endpoint hints.
 */

import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

interface RatingSliderProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
}

function getRatingColor(rating: number): string {
  if (rating <= 2) return "text-red-500";
  if (rating <= 4) return "text-orange-500";
  if (rating <= 6) return "text-yellow-500";
  if (rating <= 8) return "text-emerald-500";
  return "text-green-500";
}

function getRatingBgColor(rating: number): string {
  if (rating <= 2) return "bg-red-500/10 dark:bg-red-600/10";
  if (rating <= 4) return "bg-orange-500/10 dark:bg-orange-600/10";
  if (rating <= 6) return "bg-yellow-500/10 dark:bg-yellow-600/10";
  if (rating <= 8) return "bg-emerald-500/10 dark:bg-emerald-600/10";
  return "bg-green-500/10 dark:bg-green-600/10";
}

function getRatingLabel(rating: number): string {
  if (rating === 1) return "Awful";
  if (rating === 2) return "Very Bad";
  if (rating === 3) return "Bad";
  if (rating === 4) return "Below Average";
  if (rating === 5) return "Okay";
  if (rating === 6) return "Decent";
  if (rating === 7) return "Good";
  if (rating === 8) return "Great";
  if (rating === 9) return "Wonderful";
  if (rating === 10) return "Bliss";
  return "";
}

export function RatingSlider({
  value,
  onChange,
  className,
}: RatingSliderProps) {
  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-center">
        <div
          className={cn(
            "inline-flex items-center gap-2 rounded-full px-4 py-2 transition-colors",
            getRatingBgColor(value)
          )}
        >
          <span className={cn("text-3xl font-bold", getRatingColor(value))}>
            {value}
          </span>
          <span className="text-sm font-medium text-muted-foreground">
            {getRatingLabel(value)}
          </span>
        </div>
      </div>
      <div className="px-1">
        <Slider
          value={[value]}
          onValueChange={(vals) => onChange(vals[0])}
          min={1}
          max={10}
          step={1}
          aria-label="Relationship rating"
        />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground px-1">
        <span>1 — Awful</span>
        <span>10 — Bliss</span>
      </div>
    </div>
  );
}
